"use client";
import { useAuth } from "@/contexts/AuthContext";
import useRecaptcha from "@/hooks/useRecaptcha";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import SocialLogins from "./SocialLogins";
import { Button } from "./ui/Button";
import { Input } from "./ui/input";

export default function Login({ onForgotPassword, onRegister }) {
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const t = useTranslationDetector();
  const { getCaptchaToken } = useRecaptcha();

  // Zod schema with dynamic messages from next-intl
  const schema = z.object({
    email: z
      .string()
      .email(t("validations.email"))
      .min(1, t("validations.emailmin")),
    password: z
      .string()
      .min(6, t("validations.passwordmin"))
      .max(50, t("validations.passwordmax")),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  const singIn = async (data) => {
    setError(null);
    setLoading(true);
    try {
      const RecaptchaKey = await getCaptchaToken("signin");
      const response = await login({ ...data, RecaptchaKey });

      if (!response.success) {
        setError(response.error);
      }
    } catch (error) {
      setError(t(`${error.response.data.Type}.${error.response.data.Name}`));
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    singIn({
      ...data,
      AuthenticationType: 0,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
      <div className="flex flex-col gap-4">
        <Input
          label={t("loginmodal.email")}
          id="email"
          type="email"
          {...register("email")}
          error={errors.email?.message}
        />

        <div className="flex flex-col items-end gap-3">
          <Input
            className="w-full"
            label={t("loginmodal.password")}
            id="password"
            type="password"
            {...register("password")}
            error={errors.password?.message}
          />
          <Button
            variant="link"
            size="sm"
            color="black"
            onClick={onForgotPassword}
          >
            {t("loginmodal.forgotpassword")}
          </Button>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <Button type="submit" loading={loading} className="w-full" size="lg">
          {t("loginmodal.login")}
        </Button>
      </div>

      <div className="flex items-center justify-between gap-2">
        <span className="border-primary-weak w-full border-b"></span>
        <span className="px-2">{t("common.or")}</span>
        <span className="border-primary-weak w-full border-b"></span>
      </div>

      <SocialLogins />

      <div className="flex items-center justify-center gap-2">
        <span>{t("loginmodal.dontHaveAccount")}</span>
        <Button variant="link" color="primary" onClick={onRegister}>
          {t("loginmodal.register")}
        </Button>
      </div>
    </form>
  );
}
